
import { Component } from '@angular/core';
import { AccessHistoryDialogComponent } from '@components/access-history-dialog/access-history-dialog.component';
import { AuthorizedUsersDialogComponent } from '@components/authorized-users-dialog/authorized-users-dialog.component';
import {
  GroupAuthorization,
  GroupDto,
} from '@models/authorizations/group.model';
import { SignInDto } from '@models/users/sign-in.dto';
import { SubscriberDto } from '@models/users/subscriber.dto';
import { AccountService } from '@services/account.service';
import { AuthorizationService } from '@services/authorization.service';
import { MessageService } from '@services/message.service';
import { SubscriberService } from '@services/subscriber.service';
import { clone } from '@utils/clone.function';
import { isEmpty } from '@utils/is-empty.function';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, finalize, forkJoin, of, tap } from 'rxjs';
import { FunctionalityListDialogComponent } from './functionality-list-dialog/functionality-list-dialog.component';

@Component({
  selector: 'ebs-groups',
  templateUrl: './groups.component.html',
  styleUrls: ['./groups.component.scss'],
})
export class GroupsComponent {
  creating: SignInDto | undefined;

  public search: string = '';
  public groupsLoading = true;
  public functionalityDialogOpen = false;

  // Nuova dialog di creazione gruppo
  public newGroupDialogVisible = false;
  public newGroupSaving = false;
  public newGroup: GroupDto = this.createEmptyGroup();

  public get totals(): {
    total: number;
    enabled: number;
  } {
    return {
      total: this.subscribers.length,
      enabled: this.auths.filter(
        (elem) => elem.signId !== undefined,
      ).length,
    };
  }

  public get newGroupValid(): boolean {
    return (
      this.newGroup.name.trim().length > 0 &&
      this.newGroup.description.trim().length > 0 &&
      Number.isInteger(this.newGroup.passwordExpirationInDays) &&
      this.newGroup.passwordExpirationInDays > 0
    );
  }

  public subscribers: SubscriberDto[] = [];
  public auths: SignInDto[] = [];

  public groups: GroupDto[] = [];
  public selectedGroup: GroupDto | null = null;
  clonedSignInDtos: { [s: number]: SignInDto } = {};

  public items: MenuItem[] = [
    {
      command: () => this.addNewGroup(),
      icon: PrimeIcons.PLUS,
    },
    {
      command: () => this.showAuthorizations(-1),
      icon: PrimeIcons.TAGS,
    },
  ];

  sortExpDate: boolean = false;
  sortLastLoginDate: boolean = false;

  constructor(
    private authService: AuthorizationService,
    private subscriberService: SubscriberService,
    private accountService: AccountService,
    public dialogService: DialogService,
    private messageService: MessageService,
  ) {
    this.loadAll();
  }

  private createEmptyGroup(): GroupDto {
    return {
      groupId: null,
      name: '',
      description: '',
      isEnabled: true,
      authorizations: [],
      passwordExpirationInDays: 180,
    };
  }

  loadAll(): void {
    this.groupsLoading = true;

    forkJoin({
      groups: this.authService.groupList(),
      subscribers: this.subscriberService.getSubscribers(),
      signs: this.accountService.getAuthList(),
    })
      .pipe(
        catchError(() => {
          this.messageService.error(
            'Accessi e autorizzazioni',
            'Errore durante il caricamento dei dati',
          );
          return of(null);
        }),
        finalize(() => {
          this.groupsLoading = false;
        }),
      )
      .subscribe((result) => {
        if (result === null) {
          return;
        }

        this.groups = result.groups;
        this.subscribers = result.subscribers;
        this.buildAuths(result.signs);
      });
  }

  private buildAuths(signs: SignInDto[]): void {
    this.auths = this.subscribers.map((sub) => {
      let sign = signs.find(
        (elem) => elem.email === sub.email,
      );

      if (sign === undefined) {
        sign = {
          email: sub.email,
          group: null,
          passwordExpirationDate: new Date(0),
        };
      }

      sign.subscriber = sub;
      sign.passwordExpirationDate = new Date(
        sign.passwordExpirationDate,
      );
      sign.latestLoginDate = sign.latestLoginDate
        ? new Date(sign.latestLoginDate)
        : undefined;

      return sign;
    });
  }

  loadGroups(): void {
    this.authService.groupList().subscribe({
      next: (result: GroupDto[]) => {
        this.groups = result;
      },
      error: () => {
        this.messageService.error(
          'Gruppi',
          'Errore durante il caricamento dei gruppi',
        );
      },
    });
  }

  loadUsers(): void {
    this.groupsLoading = true;

    forkJoin({
      subscribers: this.subscriberService.getSubscribers(),
      signs: this.accountService.getAuthList(),
    })
      .pipe(
        catchError(() => {
          this.messageService.error(
            'Accessi utenti',
            'Errore durante il caricamento degli utenti',
          );
          return of(null);
        }),
        finalize(() => {
          this.groupsLoading = false;
        }),
      )
      .subscribe((result) => {
        if (result === null) {
          return;
        }

        this.subscribers = result.subscribers;
        this.buildAuths(result.signs);
      });
  }

  expirationSortChanged(): void {
    this.sortExpDate = !this.sortExpDate;

    this.auths = Object.assign(
      [],
      this.auths.sort(
        (auth1: SignInDto, auth2: SignInDto) => {
          return this.sortExpDate
            ? auth1.passwordExpirationDate.getDate() -
                auth2.passwordExpirationDate.getDate()
            : auth2.passwordExpirationDate.getDate() -
                auth1.passwordExpirationDate.getDate();
        },
      ),
    );
  }

  lastLoginSortChanged(): void {
    this.sortLastLoginDate = !this.sortLastLoginDate;

    this.auths = Object.assign(
      [],
      this.auths.sort(
        (auth1: SignInDto, auth2: SignInDto) => {
          const auth1Last = auth1.latestLoginDate
            ? new Date(auth1.latestLoginDate)
            : new Date(0);

          const auth2Last = auth2.latestLoginDate
            ? new Date(auth2.latestLoginDate)
            : new Date(0);

          return this.sortLastLoginDate
            ? auth1Last.getDate() - auth2Last.getDate()
            : auth2Last.getDate() - auth1Last.getDate();
        },
      ),
    );
  }

  resetPasswordExpiration(sign: SignInDto): void {
    sign.passwordExpirationDate = new Date();
    this.accountService.modifySignIn([sign]).subscribe();
  }

  resetPassword(sign: SignInDto): void {
    this.accountService.resetPassword(sign.signId!).subscribe();
  }

  onRowEditSave(signIn: SignInDto): void {
    this.accountService.modifySignIn([signIn]).subscribe(() => {
      delete this.clonedSignInDtos[signIn.signId as number];
      this.loadUsers();
    });
  }

  removeSignIn(signIn: SignInDto): void {
    this.accountService.removeSignIn([signIn]).subscribe(() => {
      this.loadUsers();
    });
  }

  onRowEditInit(signIn: SignInDto): void {
    this.clonedSignInDtos[signIn.signId as number] = {
      ...signIn,
    };
  }

  onRowEditCancel(signIn: SignInDto, index: number): void {
    this.auths[index] =
      this.clonedSignInDtos[signIn.signId as number];

    delete this.clonedSignInDtos[signIn.signId as number];
  }

  editGroup(group: GroupDto | null): void {
    this.selectedGroup = clone(group);
  }

  cancel(index: number): void {
    if (isEmpty(this.groups[index].groupId)) {
      this.groups.pop();
    } else {
      this.groups[index] = clone(this.selectedGroup);
    }

    this.selectedGroup = null;
  }

  modifyGroup(index: number): void {
    this.saveGroupChanges(this.groups[index]);
  }

  saveGroupChanges(group: GroupDto): void {
    this.authService
      .modifyGroup([group])
      .pipe(
        catchError((err) => {
          this.loadGroups();
          this.selectedGroup = null;
          this.messageService.error(
            'Modifica gruppo',
            'Errore durante la modifica',
          );
          throw err;
        }),
      )
      .subscribe(() => {
        this.loadGroups();
        this.selectedGroup = null;
        this.messageService.success(
          'Modifica gruppo',
          'Modifica avvenuta con successo',
        );
      });
  }

  removeGroup(group: GroupDto, index: number): void {
    if (
      group.groupId === this.accountService.group?.groupId
    ) {
      this.messageService.warn(
        'Eliminazione gruppo',
        'Non puoi eliminare il tuo gruppo',
      );
    } else {
      this.authService
        .removeGroup([group])
        .pipe(
          catchError((err) => {
            this.selectedGroup = null;
            this.messageService.error(
              'Eliminazione gruppo',
              "Errore durante l'eliminazione",
            );
            throw err;
          }),
        )
        .subscribe(() => {
          this.groups.splice(index, 1);
          this.selectedGroup = null;
          this.messageService.success(
            'Eliminazione gruppo',
            'Eliminazione avvenuta con successo',
          );
        });
    }
  }

  showAuthorizations(index: number): void {
    if (this.functionalityDialogOpen) {
      return;
    }

    this.functionalityDialogOpen = true;

    const dialogRef: DynamicDialogRef = this.dialogService.open(
      FunctionalityListDialogComponent,
      {
        header:
          index > -1
            ? 'Selezione autorizzazioni'
            : 'Gestione funzionalità',
        maximizable: false,
        draggable: false,
        height: '100%',
        width: '100%',
        closable: true,
        closeOnEscape: false,
        data: {
          group: index > -1 ? this.groups[index] : null,
        },
      },
    );

    dialogRef.onClose
      .pipe(
        tap((result: GroupAuthorization[]) => {
          if (!result) {
            return;
          }

          if (index > -1 && this.groups[index]) {
            this.groups[index].authorizations = result;
          }
        }),
        finalize(() => {
          this.functionalityDialogOpen = false;
        }),
      )
      .subscribe({
        error: () => {
          this.functionalityDialogOpen = false;
        },
      });
  }

  modifyFunctionalities(): void {}

  modifyAuthorizedUsers(): void {
    this.accountService.getAuthList().subscribe((authList) => {
      const selectedUsers: any[] = authList.filter(
        (elem: any) =>
          !isEmpty(elem.group) &&
          elem.group.groupId === this.selectedGroup!.groupId,
      );

      const allUsers: any[] = authList.filter(
        (elem: any) =>
          isEmpty(elem.group) ||
          elem.group.groupId === this.selectedGroup!.groupId,
      );

      const dialogRef: DynamicDialogRef = this.dialogService.open(
        AuthorizedUsersDialogComponent,
        {
          maximizable: false,
          draggable: false,
          height: '100%',
          width: '100%',
          closable: true,
          closeOnEscape: false,
          data: {
            selectedUsers,
            allUsers,
            allowSimplifiedView: false,
          },
        },
      );

      dialogRef.onClose.subscribe((authorized: any[]) => {
        if (!authorized) {
          return;
        }

        const presentIds = authorized.map(
          (elem) => elem.signId,
        );

        allUsers
          .filter(
            (elem) => !presentIds.includes(elem.signId),
          )
          .forEach((elem) => {
            elem.group = null;
          });

        allUsers
          .filter(
            (elem) => presentIds.includes(elem.signId),
          )
          .forEach((elem) => {
            elem.group = this.selectedGroup;
          });

        this.accountService.modifySignIn(allUsers).subscribe(() => {
          this.messageService.success(
            'Modifica gruppo',
            'Modifica avvenuta con successo',
          );
        });
      });
    });
  }

  viewUserHistory(sign: SignInDto): void {
    this.dialogService.open(
      AccessHistoryDialogComponent,
      {
        header: 'Storico attività utente',
        maximizable: false,
        draggable: false,
        width: 'min(1180px, 96vw)',
        height: 'min(700px, 88dvh)',
        contentStyle: {
          overflow: 'hidden',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '0',
        },
        closable: true,
        closeOnEscape: false,
        data: {
          sign,
        },
      },
    );
  }

  confirmCreate(signIn: SignInDto): void {
    if (signIn !== undefined) {
      this.accountService.createSignIn([signIn]).subscribe(() => {
        this.creating = undefined;
        this.loadUsers();
      });
    }
  }

  /**
   * Apre la dialog senza aggiungere card vuote.
   */
  addNewGroup(): void {
    if (this.newGroupSaving) {
      return;
    }

    this.newGroup = this.createEmptyGroup();
    this.newGroupDialogVisible = true;
  }

  /**
   * Chiude la dialog e scarta i dati non salvati.
   */
  cancelNewGroup(): void {
    if (this.newGroupSaving) {
      return;
    }

    this.newGroupDialogVisible = false;
    this.newGroup = this.createEmptyGroup();
  }

  /**
   * Salva il nuovo gruppo tramite il servizio già usato
   * per la creazione dalla card.
   */
  saveNewGroup(): void {
    if (!this.newGroupValid || this.newGroupSaving) {
      return;
    }

    const group: GroupDto = {
      ...this.newGroup,
      name: this.newGroup.name.trim(),
      description: this.newGroup.description.trim(),
      authorizations: [],
    };

    this.newGroupSaving = true;

    this.authService
      .modifyGroup([group])
      .pipe(
        finalize(() => {
          this.newGroupSaving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.newGroupDialogVisible = false;
          this.newGroup = this.createEmptyGroup();

          this.loadGroups();

          this.messageService.success(
            'Nuovo gruppo',
            'Gruppo creato con successo',
          );
        },
        error: () => {
          this.messageService.error(
            'Nuovo gruppo',
            'Errore durante la creazione del gruppo',
          );
        },
      });
  }
}
